import { userAtom } from '@/global-store';
import useFormSchema from '@/hooks/useFormSchema';
import { Alert } from '@eye-able/ui';
import PhoneInput from '@components/PhoneInput.jsx';
import { useNotification } from '@hooks/useNotification.jsx';
import {
  useCreateUser,
  useFetchOrganisations,
  usePostOrganisationAdmin,
  useUpdateUser,
} from '@http/queries.js';
import {
  Button,
  Collapse,
  MenuItem,
  TextField as TextFieldMUI,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { IconEdit, IconUserPlus, IconUsersGroup } from '@tabler/icons-react';
import { useState } from 'react';
import { useAtom } from 'jotai';
import { Autocomplete, Select, Switches, TextField } from 'mui-rff';
import nanoclone from 'nanoclone';
import { Form } from 'react-final-form';
import * as yup from 'yup';
import DynamicDialog from './DynamicDialog';
import { noLeadingOrTrailingSpaces } from '@/helpers/yupValidation.js';
import OrgDialog from '@/components/OrgDialog';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';
import { T } from '@/i18n/T';

export default function UserDialog({
  userData,
  onSuccess,
  useOrgFetch = () => useFetchOrganisations('all'),
  useOrgPost = usePostOrganisationAdmin,
  getOrgOptionDisabled,
  hideOrgAdminToggle = false,
  showOrgSelect = false,
  hintText,
  ...dialogProps
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState(null);

  const { i18n, t } = useInlineTranslation();
  const notification = useNotification();
  const [currentUser] = useAtom(userAtom);

  const createUserMutation = useCreateUser();
  const editUserMutation = useUpdateUser();

  const { data: orgList, isLoading } = useOrgFetch();

  const languages = [
    {
      label: t('de'),
      value: 'de',
    },
    {
      label: t('en'),
      value: 'en',
    },
  ];

  let editUser = !!userData;

  const defaultValues = {
    role: 'customer',
    mailLang: i18n.resolvedLanguage === 'de' ? 'de' : 'en',
    sendSetupMail: !editUser,
    primaryOrganisation: showOrgSelect ? null : currentUser.primaryOrganisation,
    orgAdmin:
      userData?.orgAdmin || currentUser.isAdmin || currentUser.isSalesAdmin,
    firstName: '',
    lastName: '',
    company: showOrgSelect ? null : currentUser.company,
    email: '',
    phone: '',
  };

  if (!userData) {
    userData = defaultValues;
  }

  const initialValues = {
    ...defaultValues,
    ...userData,
  };

  const showRestrictedLicensePromotionWarning =
    editUser && Boolean(userData.constraintLicenses) && !userData.orgAdmin;

  const { validate, required } = useFormSchema(
    yup.object({
      firstName: noLeadingOrTrailingSpaces('First Name').max(128).required(),
      lastName: noLeadingOrTrailingSpaces('Last Name').max(128).required(),
      email: yup.string().max(128).email().required(),
      mailLang: yup.string().required().default('de'),
      sendSetupMail: yup.boolean().default(false),
      role: yup
        .string()
        .oneOf(['customer', 'partner', 'admin', 'publicAdmin', 'salesAdmin'])
        .default('customer'),
      primaryOrganisation:
        userData.primaryOrganisation || !editUser
          ? yup.number().min(1).required()
          : yup.number().min(1).nullable(),
      orgAdmin: yup
        .boolean()
        .default(
          userData.orgAdmin || currentUser.isAdmin || currentUser.isSalesAdmin,
        ),
    }),
  );

  async function createUser(body, form) {
    try {
      const res = await createUserMutation.mutateAsync(body);

      const message = t('userCreatedSuccess');
      notification.success(message, 15000);

      form.restart();
      setIsDialogOpen(false);

      onSuccess?.(res);
      return true;
    } catch (error) {
      let message = t('userCreatedFailed');

      if (error?.message?.includes('Email already')) {
        message = t('mailAlreadyExists');
      }

      notification.error(message, 15000);
      console.error(message + '\n\n' + JSON.stringify(error, null, 2));
    }
  }

  async function updateUser(body, form) {
    try {
      const res = await editUserMutation.mutateAsync({
        modifyUserApiKey: userData.apiKey,
        ...body,
      });

      const message = t('userUpdatedSuccess');
      notification.success(message, 15000);

      form.restart();
      setIsDialogOpen(false);

      onSuccess?.(res);
      return true;
    } catch (error) {
      const message = t('userUpdatedFailed');

      notification.error(message, 15000);
      console.error(message + '\n\n' + JSON.stringify(error, null, 2));
    }
  }

  function submit(values, form) {
    console.log('submit', values);
    const body = { ...userData, ...values };

    body.company = body.primaryOrganisationObject?.name || null;
    delete body.fullName;

    if (editUser) {
      return updateUser(body, form);
    } else {
      return createUser(body, form);
    }
  }

  function handleCreateOrganization(inputValue) {
    setNewOrgName(inputValue);
    setIsDialogOpen(false);
    setIsOrgDialogOpen(true);
  }

  function handleOrgCreationClose() {
    setNewOrgName(null);
    setIsOrgDialogOpen(false);
    setIsDialogOpen(true);
  }

  return (
    <Form
      validate={validate}
      key={`${editUser}-${isLoading}`}
      initialValues={initialValues}
      onSubmit={submit}
      render={({ handleSubmit, form, values }) => (
        <form noValidate onSubmit={handleSubmit}>
          <OrgControlledDialog
            form={form}
            isOpen={isOrgDialogOpen}
            onClose={handleOrgCreationClose}
            usePost={useOrgPost}
            name={newOrgName}
          />
          <DynamicDialog
            onConfirm={handleSubmit}
            title={editUser ? <T k='editUser' /> : <T k='addUser' />}
            activatorText={editUser ? <T k='editUser' /> : <T k='addUser' />}
            activatorProps={{
              color: 'primary',
              variant: 'contained',
              startIcon: editUser ? <IconEdit /> : <IconUserPlus />,
            }}
            overwriteOpen={isDialogOpen}
            onOpen={() => setIsDialogOpen(true)}
            onCancel={() => setIsDialogOpen(false)}
            fullWidth
            {...dialogProps}
          >
            <Grid
              container
              sx={{
                alignItems: 'center',
                gap: 2.5,
                justifyContent: 'center',
                my: 2.5,
                px: { xs: 3, md: 4, xl: 5.5 },
              }}
            >
              {showRestrictedLicensePromotionWarning && values.orgAdmin ? (
                <Alert variant='warning'>
                  {t('orgAdminPromotionClearsRestrictedLicensesWarning')}
                </Alert>
              ) : null}
              {hintText && <p>{hintText}</p>}
              {showOrgSelect && (
                <OrgAutocomplete
                  form={form}
                  getOptionDisabled={getOrgOptionDisabled}
                  orgList={orgList ?? []}
                  onCreateOrganization={handleCreateOrganization}
                  required={required.primaryOrganisation}
                />
              )}
              <Collapse
                in={!showOrgSelect || editUser || values.primaryOrganisation}
                unmountOnExit
              >
                <Grid
                  container
                  sx={{
                    alignItems: 'center',
                    gap: 2.5,
                    justifyContent: 'center',
                  }}
                >
                  <TextField
                    autoComplete='new-password'
                    fullWidth
                    label={<T k='firstName' />}
                    name='firstName'
                    required={required.firstName}
                  />
                  <TextField
                    autoComplete='new-password'
                    fullWidth
                    label={<T k='lastName' />}
                    name='lastName'
                    required={required.lastName}
                  />
                  <TextField
                    autoComplete='new-password'
                    fullWidth
                    label='Email'
                    name='email'
                    required={required.email}
                  />
                  <PhoneInput
                    name='phone'
                    fieldProps={{ parse: (value) => value || '' }}
                  />
                  {/* <CountrySelect /> */}

                  <Select
                    formControlProps={{ fullWidth: true }}
                    fullWidth
                    label={<T k='mailLang' />}
                    name='mailLang'
                  >
                    {languages.map((lang) => (
                      <MenuItem key={lang.value} value={lang.value}>
                        {lang.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {currentUser.isAdmin && userData.id !== currentUser.id && (
                    <TextField
                      autoComplete='new-password'
                      fullWidth
                      label={<T k='role' />}
                      name='role'
                      required={required.role}
                      select
                    >
                      <MenuItem value='customer'>Customer</MenuItem>

                      <MenuItem value='publicAdmin'>PublicAdmin</MenuItem>

                      <MenuItem value='partner'>Partner</MenuItem>

                      <MenuItem value='salesAdmin'>SalesAdmin</MenuItem>

                      <MenuItem value='admin'>ADMIN</MenuItem>
                    </TextField>
                  )}
                  {!editUser &&
                    (currentUser.isAdmin || currentUser.isSalesAdmin) && (
                      <>
                        <Switches
                          name='sendSetupMail'
                          data={{
                            label: t('sendMailToUser'),
                            value: false,
                          }}
                        />
                      </>
                    )}
                  {editUser &&
                    userData.id !== currentUser.id &&
                    (currentUser.isAdmin || currentUser.isOrgAdmin) && (
                      <Switches
                        name='isActive'
                        data={{
                          label: `${t('isActive')}?`,
                          value: userData.isActive,
                        }}
                      />
                    )}
                  {!hideOrgAdminToggle &&
                    (currentUser.isAdmin || currentUser.isOrgAdmin) && (
                      <Switches
                        name='orgAdmin'
                        data={{
                          label: (
                            <Tooltip title={t('orgAdminDescription')}>
                              <span>
                                <T k='orgAdmin' />?
                              </span>
                            </Tooltip>
                          ),
                          value:
                            userData.orgAdmin ||
                            currentUser.isAdmin ||
                            currentUser.isSalesAdmin,
                        }}
                      />
                    )}
                </Grid>
              </Collapse>
            </Grid>
          </DynamicDialog>
        </form>
      )}
    />
  );
}

function OrgControlledDialog({ form, isOpen, onClose, usePost, name }) {
  function handleOrgCreationSuccess(newOrg, form) {
    const currentFormValues = nanoclone(form.getState().values);

    form.reset();

    setTimeout(() => {
      form.batch(() => {
        if (currentFormValues.firstName) {
          form.change('firstName', currentFormValues.firstName);
        }
        if (currentFormValues.lastName) {
          form.change('lastName', currentFormValues.lastName);
        }
        if (currentFormValues.email) {
          form.change('email', currentFormValues.email);
        }
        if (currentFormValues.phone) {
          form.change('phone', currentFormValues.phone);
        }
        if (currentFormValues.role) {
          form.change('role', currentFormValues.role);
        }
        if (currentFormValues.mailLang) {
          form.change('mailLang', currentFormValues.mailLang);
        }

        form.change('primaryOrganisation', newOrg.id);
      });
    }, 350);

    onClose();
  }

  return (
    <OrgDialog
      usePost={usePost}
      onSuccess={(newOrg) => handleOrgCreationSuccess(newOrg, form)}
      hideActivator
      overwriteOpen={isOpen}
      onCancel={onClose}
      initialValues={{ name }}
    />
  );
}

function OrgAutocomplete({
  getOptionDisabled,
  orgList,
  form,
  onCreateOrganization,
  required,
}) {
  const { t } = useInlineTranslation();

  function filterOrgOptions(options, params) {
    const safeOptions = options || [];
    const inputValue = params.inputValue.trim().toLowerCase();

    if (inputValue === '') {
      return safeOptions;
    }

    const filtered = safeOptions.filter((option) =>
      option.name?.toLowerCase()?.includes(inputValue),
    );

    const exactMatch = safeOptions.find(
      (option) => option.name?.toLowerCase() === inputValue,
    );

    if (!exactMatch && inputValue) {
      filtered.push({
        id: 'add',
        inputValue: params.inputValue.trim(),
        isCreateOption: true,
      });
    }

    return filtered;
  }

  return (
    <Autocomplete
      name='primaryOrganisation'
      autoHighlight
      disableClearable
      fullWidth
      getOptionValue={(option) => option?.id}
      getOptionLabel={(option) => option?.name || ''}
      {...(getOptionDisabled && {
        getOptionDisabled,
      })}
      openOnFocus
      isOptionEqualToValue={(option, value) =>
        typeof value === 'object' ? option.id === value.id : option.id === value
      }
      noOptionsText={<T k='noOptions' />}
      options={orgList}
      onChange={(_, newValue) => {
        if (newValue?.inputValue) return;
        form.change('primaryOrganisation', newValue?.id);
      }}
      filterOptions={filterOrgOptions}
      renderOption={(props, option) => {
        if (option.isCreateOption) {
          return (
            <li
              {...props}
              key='add-organization'
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCreateOrganization(option.inputValue);
              }}
            >
              <Button fullWidth startIcon={<IconUsersGroup />}>
                <T k='createOrgNamed' options={{ name: option.inputValue }} />
              </Button>
            </li>
          );
        }

        return (
          <li {...props} key={option.id}>
            {option.name}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextFieldMUI
          {...params}
          required={required}
          label={<T k='organisation' />}
          placeholder={t('selectOrCreateOrganisation')}
          InputLabelProps={{ shrink: true }}
        />
      )}
    />
  );
}
